import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import {
  Card,
  CardContent,
  CardHeader,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { Checkbox } from "@jambushrushti/components/ui/checkbox";
import { useToast } from "@jambushrushti/hooks/use-toast";

import {
  useAdminAuth,
  UserRole,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
} from "@jambushrushti/contexts/AdminAuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Scroll,
  Mail,
  Lock,
  User,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Crown,
  Shield,
  GraduationCap,
  BookOpen,
  UserCheck,
  Globe,
  Ticket,
} from "lucide-react";

// Extended roles for display (includes Public Visitor which isn't a DB role)
type DisplayRole = UserRole | "visitor" | "ticket";

const DISPLAY_ROLES: {
  value: DisplayRole;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "superadmin",
    label: "Super Admin",
    description: "Full system access, manage admins & settings",
    icon: <Crown className="h-4 w-4 text-amber-500" />,
  },
  {
    value: "admin",
    label: "Admin",
    description: "CMS control, content management, approvals",
    icon: <Shield className="h-4 w-4 text-red-500" />,
  },
  {
    value: "scholar",
    label: "Scholar",
    description: "Research portal, submissions, access requests",
    icon: <GraduationCap className="h-4 w-4 text-blue-500" />,
  },
  {
    value: "librarian",
    label: "Librarian",
    description: "Library management system access",
    icon: <BookOpen className="h-4 w-4 text-gray-600" />,
  },
  {
    value: "lms",
    label: "LMS (Learning)",
    description: "Learning portal, courses, lectures & materials",
    icon: <UserCheck className="h-4 w-4 text-primary" />,
  },
  {
    value: "ticket",
    label: "Ticket Management",
    description: "Support tickets, SLA tracking & issue resolution",
    icon: <Ticket className="h-4 w-4 text-green-600" />,
  },
];

const Auth = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    login,
    signup,
    isAuthenticated,
    isLoading: authLoading,
    user,
  } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"role" | "login" | "register">(
    "role"
  );
  const [selectedRole, setSelectedRole] = useState<DisplayRole | "">("");

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register state
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Only redirect after login completes with a valid redirect path
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  const getRedirectPath = (role: UserRole | DisplayRole | ""): string => {
    switch (role) {
      case "superadmin":
      case "admin":
        return "/admin/dashboard";
      case "librarian":
        return "/lms/dashboard";
      case "scholar":
        return "/research";
      case "lms":
        return "/learning/dashboard";
      case "ticket":
        return "/tickets/dashboard";
      default:
        return "/";
    }
  };

  const handleRoleSelect = (role: DisplayRole) => {
    setSelectedRole(role);
    // DEMO MODE: Direct navigation to dashboard based on role
    const redirectPath = getRedirectPath(role);
    navigate(redirectPath, { replace: true });
  };

  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginEmail || !loginPassword) {
      toast({
        title: "Missing credentials",
        description: "Please enter your email and password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const result = await login(loginEmail, loginPassword);

    if (result.success && result.redirectTo) {
      toast({
        title: "Welcome back!",
        description: "You have been logged in successfully.",
      });
      setRedirectTo(result.redirectTo);
    } else {
      toast({
        title: "Invalid Credentials",
        description: result.error || "Please check your email and password",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registerName || !registerEmail || !registerPassword) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (registerPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }

    if (registerPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    if (!agreeTerms) {
      toast({
        title: "Terms Required",
        description: "Please agree to the terms and conditions.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const result = await signup(registerEmail, registerPassword, registerName);

    if (result.success) {
      toast({
        title: "Account Created!",
        description: "You can now log in with your credentials.",
      });
      setActiveTab("login");
      setLoginEmail(registerEmail);
      setRegisterPassword("");
      setConfirmPassword("");
    } else {
      toast({
        title: "Registration Failed",
        description:
          result.error || "Could not create account. Please try again.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-gradient-hero lotus-pattern flex min-h-[calc(100vh-200px)] items-center py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-md">
            {/* Logo */}
            <div className="mb-8 text-center">
              <Link to="/" className="inline-flex items-center gap-3">
                <div className="shadow-glow flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold">
                  <Scroll className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="font-display text-2xl font-bold text-foreground">
                  Jambushrusti
                </span>
              </Link>
            </div>

            <Card variant="elevated" className="">
              <Tabs
                value={activeTab}
                onValueChange={v =>
                  setActiveTab(v as "role" | "login" | "register")
                }
              >
                <CardHeader className="pb-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="role" className="text-xs sm:text-sm">
                      <Globe className="mr-1 hidden h-4 w-4 sm:inline" />
                      Role
                    </TabsTrigger>
                    <TabsTrigger value="login" className="text-xs sm:text-sm">
                      <Lock className="mr-1 hidden h-4 w-4 sm:inline" />
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger
                      value="register"
                      className="text-xs sm:text-sm"
                    >
                      <User className="mr-1 hidden h-4 w-4 sm:inline" />
                      Register
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                <CardContent>
                  {/* Role Selection Tab */}
                  <TabsContent value="role" className="mt-0">
                    <div className="space-y-4">
                      <Label>Select Your Role</Label>
                      <Select
                        value={selectedRole}
                        onValueChange={value =>
                          handleRoleSelect(value as DisplayRole)
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose a role to login..." />
                        </SelectTrigger>
                        <SelectContent>
                          {DISPLAY_ROLES.map(role => (
                            <SelectItem
                              key={role.value}
                              value={role.value}
                              className="py-3"
                            >
                              <div className="flex items-start gap-3">
                                {role.icon}
                                <div>
                                  <div className="font-medium">
                                    {role.label}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {role.description}
                                  </div>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-4 text-center text-sm text-muted-foreground">
                        Select your role to proceed to the appropriate login or
                        access the public website.
                      </p>
                    </div>
                  </TabsContent>

                  {/* Credentials Login Tab */}
                  <TabsContent value="login" className="mt-0">
                    <form
                      onSubmit={handleCredentialsLogin}
                      className="space-y-4"
                    >
                      {selectedRole && selectedRole !== "visitor" && (
                        <div className="mb-4 rounded-lg bg-muted p-3">
                          <p className="text-sm text-muted-foreground">
                            Logging in as:{" "}
                            <span className="font-medium text-foreground">
                              {
                                DISPLAY_ROLES.find(
                                  r => r.value === selectedRole
                                )?.label
                              }
                            </span>
                          </p>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="login-email"
                            type="email"
                            required
                            className="pl-10"
                            placeholder="your@email.com"
                            value={loginEmail}
                            onChange={e => setLoginEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            required
                            className="pl-10 pr-10"
                            placeholder="••••••••"
                            value={loginPassword}
                            onChange={e => setLoginPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox id="remember" />
                          <label
                            htmlFor="remember"
                            className="cursor-pointer text-sm text-muted-foreground"
                          >
                            Remember me
                          </label>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        variant="hero"
                        className="w-full"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          <>
                            Sign In
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Register Tab */}
                  <TabsContent value="register" className="mt-0">
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-name">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="register-name"
                            required
                            className="pl-10"
                            placeholder="Your full name"
                            value={registerName}
                            onChange={e => setRegisterName(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="register-email"
                            type="email"
                            required
                            className="pl-10"
                            placeholder="your@email.com"
                            value={registerEmail}
                            onChange={e => setRegisterEmail(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="register-password"
                            type={showPassword ? "text" : "password"}
                            required
                            className="pl-10 pr-10"
                            placeholder="Min. 6 characters"
                            value={registerPassword}
                            onChange={e => setRegisterPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">
                          Confirm Password
                        </Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="confirm-password"
                            type="password"
                            required
                            className="pl-10"
                            placeholder="••••••••"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="terms"
                          checked={agreeTerms}
                          onCheckedChange={checked =>
                            setAgreeTerms(checked as boolean)
                          }
                        />
                        <label
                          htmlFor="terms"
                          className="cursor-pointer text-sm text-muted-foreground"
                        >
                          I agree to the{" "}
                          <Link
                            to="/terms"
                            className="text-primary hover:underline"
                          >
                            Terms
                          </Link>{" "}
                          and{" "}
                          <Link
                            to="/privacy"
                            className="text-primary hover:underline"
                          >
                            Privacy Policy
                          </Link>
                        </label>
                      </div>
                      <Button
                        type="submit"
                        variant="hero"
                        className="w-full"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating account...
                          </>
                        ) : (
                          <>
                            Create Account
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Admin access?{" "}
              <Link
                to="/admin"
                className="font-medium text-primary hover:underline"
              >
                Go to Admin Portal
              </Link>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Auth;
